import React from 'react';
import { Award, ExternalLink, Star } from 'lucide-react';
import { certifications } from '../mock';
import { useTheme } from '../context/ThemeContext';

const Certifications = () => {
  const { theme } = useTheme();

  return (
    <section id="certifications" className={`py-12 sm:py-20 px-4 sm:px-6 lg:px-8 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-[#1B3139] to-[#0f1f26]' 
        : 'bg-gradient-to-br from-gray-100 to-white'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Award className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
            <span 
              className="text-[#FF3621] font-semibold text-xs sm:text-sm uppercase tracking-wider"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              Credentials
            </span>
          </div>
          <h2 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Certifications
          </h2>
          <p 
            className={`text-base sm:text-lg max-w-2xl mx-auto px-4 ${
              theme === 'dark' ? 'text-white/60' : 'text-gray-600'
            }`}
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Industry-recognized certifications in cloud, data, and AI technologies
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {certifications.map((cert) => (
            <div 
              key={cert.id}
              className={`border rounded-lg p-4 sm:p-6 transition-all duration-300 shadow-xl hover:shadow-2xl group ${
                theme === 'dark' 
                  ? 'bg-[#1B3139] border-white/10 hover:border-[#FF3621]/50' 
                  : 'bg-white border-gray-200 hover:border-[#FF3621]/50'
              }`}
            >
              <div className="flex items-start justify-between mb-3 sm:mb-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-2 sm:p-3 bg-[#FF3621]/10 border border-[#FF3621]/30 rounded-lg">
                    <Award className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF3621]" />
                  </div>
                  <div>
                    <p 
                      className={`text-xs font-mono mb-1 ${
                        theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                      }`}
                      style={{ fontFamily: 'DM Mono, monospace' }}
                    >
                      Valid till {cert.validTill}
                    </p>
                  </div>
                </div>
                {cert.highlight && (
                  <div className="flex items-center space-x-1 bg-[#FF3621]/20 border border-[#FF3621]/40 rounded-full px-2 sm:px-3 py-1">
                    <Star className="h-3 w-3 text-[#FF3621]" />
                    <span 
                      className="text-xs text-[#FF3621] font-semibold"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      {cert.highlight}
                    </span>
                  </div>
                )}
              </div>

              <h3 
                className={`text-base sm:text-xl font-bold mb-2 group-hover:text-[#FF3621] transition-colors leading-tight ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {cert.name}
              </h3>

              <p 
                className={`text-sm sm:text-base mb-3 sm:mb-4 font-medium ${
                  theme === 'dark' ? 'text-white/70' : 'text-gray-600'
                }`}
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                {cert.issuer}
              </p>

              <div className={`flex items-center justify-between pt-3 sm:pt-4 border-t ${
                theme === 'dark' ? 'border-white/10' : 'border-gray-200'
              }`}>
                {cert.credentialId ? (
                  <span 
                    className={`text-xs font-mono truncate max-w-[200px] ${
                      theme === 'dark' ? 'text-white/40' : 'text-gray-500'
                    }`}
                    style={{ fontFamily: 'DM Mono, monospace' }}
                  >
                    ID: {cert.credentialId}
                  </span>
                ) : (
                  <span className={`text-xs ${theme === 'dark' ? 'text-white/40' : 'text-gray-400'}`}>—</span>
                )}
                <ExternalLink className={`h-4 w-4 group-hover:text-[#FF3621] transition-colors flex-shrink-0 ${
                  theme === 'dark' ? 'text-white/40' : 'text-gray-400'
                }`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Certifications;
